import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import type { SxProps, Theme } from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import { TableVirtuoso } from 'react-virtuoso'
import { useEffect, useMemo, useRef, useState } from 'react'

import { useSnackbar } from '@/hooks/useSnackbar'
import { useSetInvoiceDeclarationDateMutation } from '@/store/api/procurementApi'
import type { InvoiceDeclarationDateWrite, InvoiceListItem } from '@/types/api'
import { FORM_GRID_2COL } from '@/theme/responsive'
import { getErrorMessage } from '@/utils/error'
import {
  type ColumnFilterAnchorPosition,
  ColumnFilterIcon,
  ColumnFilterPopover,
  type ColumnFilterKind,
  FilterBar,
  FilterButton,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  useColumnFilters,
  useFilterPopover,
} from '@/components/dataList'
import ResizeHandle from '@/components/ResizeHandle'
import { useColumnWidths } from '@/hooks/useColumnWidths'
import {
  getTableSortSuffix,
  sortTableRows,
  type TableSortState,
  toggleTableSort,
} from '@/utils/tableSort'

import {
  compactDialogContentSx,
  dialogHeaderBtnSx,
  dialogTitleActionsSx,
  dialogTitleSx,
  dialogTitleTextSx,
} from '../purchase/dialogStyle'

// ---------------------------------------------------------------------------
// Props — exact signature for ListView wiring. Rows are the ticked invoice
// rows passed down from the list (no fetch needed).
// ---------------------------------------------------------------------------
interface Props {
  open: boolean
  rows: InvoiceListItem[]
  canEdit: boolean
  onClose: () => void
  onSaved: () => void
}

// ---------------------------------------------------------------------------
// Local date helper — DD-MMM-YYYY (do NOT import from ListView, re-declare).
// ---------------------------------------------------------------------------
function formatAccessDate(v: string | null | undefined): string {
  if (!v) return ''
  const d = dayjs(v)
  return d.isValid() ? d.format('DD-MMM-YYYY') : String(v)
}

/** yyyy-mm-dd (ISO date) or null from a dayjs value bound to a DatePicker. */
function toIsoDate(d: dayjs.Dayjs | null): string | null {
  return d && d.isValid() ? d.format('YYYY-MM-DD') : null
}

/** Max non-null date (as dayjs) across rows for a given field, else today. */
function maxDateOrToday(rows: InvoiceListItem[], key: 'declaration_date' | 'receive_geo_date') {
  let best: dayjs.Dayjs | null = null
  for (const r of rows) {
    const raw = r[key] as string | null | undefined
    if (!raw) continue
    const d = dayjs(raw)
    if (!d.isValid()) continue
    if (!best || d.isAfter(best)) best = d
  }
  return best ?? dayjs()
}

// ---------------------------------------------------------------------------
// Datasheet columns (/convert-table) — thứ tự + caption theo ColumnOrder của
// subform frmProcurementQryInvoiceAddDate_subForm. `checked_row` (To be Edited)
// render thành cột tick riêng, KHÔNG nằm trong TABLE_COLUMNS.
// ---------------------------------------------------------------------------
const TICK_COL_WIDTH = 60

type ColumnKind = 'text' | 'date' | 'int'
type ColumnDef = {
  key: keyof InvoiceListItem
  label: string
  kind?: ColumnKind
  align?: 'right'
  filter?: ColumnFilterKind
  wrap?: boolean
  width?: number
}

const TABLE_COLUMNS: ColumnDef[] = [
  { key: 'invoice_id', label: 'Invoice ID', kind: 'int', align: 'right', width: 90 },
  { key: 'alert_txt', label: 'Alert', width: 120 },
  { key: 'invoice_no', label: 'Invoice No.', width: 140 },
  { key: 'invoice_date', label: 'Invoice Date', kind: 'date', width: 120 },
  { key: 'contract_no', label: 'Contract No.', width: 150 },
  { key: 'contract_name', label: 'Contract Name', wrap: true, width: 220 },
  { key: 'certification', label: 'Certification', filter: 'checkboxes', width: 120 },
  { key: 'declaration_date', label: 'Declaration Date', kind: 'date', width: 130 },
  { key: 'receive_geo_date', label: 'Receive GEO Date', kind: 'date', width: 130 },
]

const cellValue = (row: InvoiceListItem, col: ColumnDef): string => {
  const val = row[col.key]
  if (val == null) return ''
  if (col.kind === 'date') return formatAccessDate(val as string)
  if (col.kind === 'int') return String(Math.round(Number(val)))
  return String(val)
}

/**
 * Declaration Date bulk-set dialog — converts Access form
 * `frmProcurementQryInvoiceAddDate` (Caption "Edit - Invoice - Declaration Date").
 *
 * Two DatePicker fields (DeclarationDate / ReceiveGEODate) apply to ALL rows
 * currently ticked in the read-only grid below. Pre-fill = MAX non-null date
 * among the passed rows, else today (Access DMax over checked rows else Now()).
 */
export default function ProcurementInvoiceDeclarationDateDialog({
  open,
  rows,
  canEdit,
  onClose,
  onSaved,
}: Props) {
  const snack = useSnackbar()

  const [declarationDate, setDeclarationDate] = useState<dayjs.Dayjs | null>(null)
  const [receiveGeoDate, setReceiveGeoDate] = useState<dayjs.Dayjs | null>(null)
  // invoice_ids currently ticked (init = all rows).
  const [tickedIds, setTickedIds] = useState<Set<number>>(new Set())

  const [setInvoiceDeclarationDate, { isLoading: saving }] = useSetInvoiceDeclarationDateMutation()

  // dataList — global Search + per-column filter + sort + resize (/convert-table).
  const [searchTerm, setSearchTerm] = useState('')
  const [sortState, setSortState] = useState<TableSortState<keyof InvoiceListItem> | null>(null)
  const { getWidth, startResize } = useColumnWidths('procurement.invoice.declaration-date.grid')
  const filterPopover = useFilterPopover<'search'>()
  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{
    key: keyof InvoiceListItem
    position: ColumnFilterAnchorPosition
  } | null>(null)

  // -------------------------------------------------------------------------
  // Reset on open. Deps kept minimal: [open, rows.length].
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!open) return
    setDeclarationDate(maxDateOrToday(rows, 'declaration_date'))
    setReceiveGeoDate(maxDateOrToday(rows, 'receive_geo_date'))
    setTickedIds(new Set(rows.map((r) => r.invoice_id)))
    setSearchTerm('')
    columnFilters.clearAll()
    setSortState(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, rows.length])

  // Filter (Search + per-column) → sort. Ticking still keys off invoice_id.
  const filteredRows = useMemo(
    () =>
      rows.filter(
        (r) =>
          rowMatchesSearch(r, searchTerm, TABLE_COLUMNS) &&
          rowMatchesAllColumnFilters(r, columnFilters.filters),
      ),
    [rows, searchTerm, columnFilters.filters],
  )
  const displayRows = useMemo(
    () => sortTableRows(filteredRows, sortState, TABLE_COLUMNS),
    [filteredRows, sortState],
  )

  const toggleSort = (key: keyof InvoiceListItem) =>
    setSortState((prev) => toggleTableSort(prev, key))

  const hasActiveFilters = !!searchTerm.trim() || columnFilters.hasAny
  const activeFilterCount = (searchTerm.trim() ? 1 : 0) + columnFilters.activeKeys.length
  const clearAllFilters = () => {
    setSearchTerm('')
    columnFilters.clearAll()
    filterPopover.close()
  }

  // Per-column popover meta.
  const popoverColumn = useMemo(
    () => (colPopover ? TABLE_COLUMNS.find((c) => c.key === colPopover.key) ?? null : null),
    [colPopover],
  )
  const popoverKind = useMemo(
    () => (popoverColumn ? inferColumnFilterKind(popoverColumn as never, rows) : 'text'),
    [popoverColumn, rows],
  )
  const popoverOptions = useMemo(
    () =>
      popoverKind === 'checkboxes' && colPopover
        ? distinctColumnValues(rows, String(colPopover.key))
        : undefined,
    [popoverKind, colPopover, rows],
  )

  // Select-all tick reflects the currently displayed (filtered) rows only.
  const displayedIds = useMemo(() => displayRows.map((r) => r.invoice_id), [displayRows])
  const allTicked = displayedIds.length > 0 && displayedIds.every((id) => tickedIds.has(id))
  const someTicked = displayedIds.some((id) => tickedIds.has(id)) && !allTicked

  const toggleRow = (id: number) =>
    setTickedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const toggleAll = () =>
    setTickedIds((prev) => {
      const next = new Set(prev)
      if (displayedIds.every((id) => next.has(id))) displayedIds.forEach((id) => next.delete(id))
      else displayedIds.forEach((id) => next.add(id))
      return next
    })

  const tickedCount = useMemo(() => tickedIds.size, [tickedIds])
  const minTableWidth = useMemo(
    () =>
      TICK_COL_WIDTH +
      TABLE_COLUMNS.reduce((sum, c) => sum + (getWidth(String(c.key)) ?? c.width ?? 120), 0),
    [getWidth],
  )

  // -------------------------------------------------------------------------
  // Save — dates apply to all currently-ticked rows.
  // -------------------------------------------------------------------------
  const savingRef = useRef(false)
  const handleSave = async () => {
    if (!canEdit || saving || savingRef.current) return
    const ids = rows.map((r) => r.invoice_id).filter((id) => tickedIds.has(id))
    if (ids.length === 0) {
      snack.warning('Please select the records you want to update.')
      return
    }
    savingRef.current = true
    try {
      const body: InvoiceDeclarationDateWrite = {
        invoice_ids: ids,
        declaration_date: toIsoDate(declarationDate),
        receive_geo_date: toIsoDate(receiveGeoDate),
      }
      const res = await setInvoiceDeclarationDate(body).unwrap()
      snack.success(res.message ?? 'Successfully Updated')
      onSaved()
      onClose()
    } catch (e) {
      snack.error(getErrorMessage(e))
    } finally {
      savingRef.current = false
    }
  }

  const handleClose = () => {
    if (saving) return
    onClose()
  }

  return (
    <Dialog
      open={open}
      onClose={(_e, reason) => {
        if (saving) return
        if (reason === 'backdropClick') return
        handleClose()
      }}
      maxWidth="xl"
      fullWidth
      scroll="paper"
    >
      <DialogTitle sx={dialogTitleSx}>
        <Typography
          component="span"
          noWrap
          title="Edit - Invoice - Declaration Date"
          sx={dialogTitleTextSx}
        >
          Edit - Invoice - Declaration Date
        </Typography>
        <Stack direction="row" spacing={0.5} sx={dialogTitleActionsSx}>
          <Button
            type="button"
            variant="contained"
            size="small"
            color="success"
            onClick={() => void handleSave()}
            disabled={!canEdit || saving}
            sx={dialogHeaderBtnSx}
          >
            {saving ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button
            type="button"
            variant="outlined"
            size="small"
            onClick={handleClose}
            disabled={saving}
            sx={dialogHeaderBtnSx}
          >
            Cancel
          </Button>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={compactDialogContentSx}>
        {!canEdit && (
          <Alert severity="info" sx={{ mb: 1.5, py: 0.25 }}>
            Read-only — you don&apos;t have edit permission for Invoice.
          </Alert>
        )}

        {/* Two date fields — apply to all ticked rows. */}
        <Box sx={[FORM_GRID_2COL, { mb: 1.5 }] as SxProps<Theme>}>
          <DatePicker
            label="DeclarationDate (for all)"
            format="DD-MMM-YYYY"
            disabled={!canEdit}
            value={declarationDate}
            onChange={(d) => setDeclarationDate(d)}
            slotProps={{
              textField: { size: 'small', fullWidth: true, InputLabelProps: { shrink: true } },
            }}
          />
          <DatePicker
            label="ReceiveGEODate (for all)"
            format="DD-MMM-YYYY"
            disabled={!canEdit}
            value={receiveGeoDate}
            onChange={(d) => setReceiveGeoDate(d)}
            slotProps={{
              textField: { size: 'small', fullWidth: true, InputLabelProps: { shrink: true } },
            }}
          />
        </Box>

        {/* Records to update — dataList template (/convert-table): FilterBar
            global Search + per-column ▽ filter + sort + resize + TableVirtuoso.
            Cột tick "To be Edited" = multi-row select (Access checked_row). */}
        <FilterBar
          hasActiveFilters={hasActiveFilters}
          onClearAll={clearAllFilters}
          activeFilterCount={activeFilterCount}
          rightSlot={
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
              RECORDS TO UPDATE ({tickedCount} selected)
            </Typography>
          }
        >
          <FilterButton
            label="Search"
            active={!!searchTerm.trim()}
            onClick={(e) => filterPopover.open('search', e)}
          />
        </FilterBar>

        <Divider sx={{ mb: 0.5 }} />

        <Box className="app-dataframe" sx={{ height: { xs: 300, sm: 400 } }}>
          <Box
            className="app-dataframe-toolbar"
            sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, alignItems: 'center' }}
          >
            <Typography variant="caption">Total: {filteredRows.length}</Typography>
            <Typography variant="caption">Loaded: {rows.length}</Typography>
          </Box>

          {/* Global Search popover */}
          <FilterPopover
            open={filterPopover.state !== null}
            anchorEl={filterPopover.state?.anchor ?? null}
            onClose={() => filterPopover.close()}
          >
            {filterPopover.isOpen('search') && (
              <SearchFilterContent
                value={searchTerm}
                onChange={setSearchTerm}
                placeholder="Invoice no, contract, certification..."
                onClear={() => filterPopover.close()}
              />
            )}
          </FilterPopover>

          {/* Per-column filter popover (1 instance) */}
          <ColumnFilterPopover
            open={colPopover !== null}
            anchorPosition={colPopover?.position ?? null}
            onClose={() => setColPopover(null)}
            label={popoverColumn?.label ?? ''}
            kind={popoverKind === 'none' ? 'text' : popoverKind}
            value={colPopover ? columnFilters.filters[String(colPopover.key)] : undefined}
            onChange={(v) => {
              if (!colPopover) return
              columnFilters.setFilter(String(colPopover.key), v)
            }}
            options={popoverOptions}
          />

          <Box sx={{ flex: 1, minHeight: 200, overflow: 'hidden' }}>
            <TableVirtuoso
              style={{ height: '100%' }}
              data={displayRows}
              components={{
                Table: (props) => (
                  <Table
                    {...props}
                    size="small"
                    stickyHeader
                    sx={{ minWidth: minTableWidth, width: '100%' }}
                  />
                ),
                TableHead: ((props: object) => <TableHead {...props} component="thead" />) as never,
                TableBody: ((props: object) => <TableBody {...props} component="tbody" />) as never,
                TableRow: (props) => {
                  const idx = Number((props as unknown as Record<string, unknown>)['data-item-index'])
                  const row = Number.isFinite(idx) ? displayRows[idx] : null
                  return (
                    <TableRow
                      {...props}
                      hover
                      selected={row != null && tickedIds.has(row.invoice_id)}
                      sx={{ cursor: 'pointer' }}
                      onClick={() => row && toggleRow(row.invoice_id)}
                    />
                  )
                },
              }}
              fixedHeaderContent={() => (
                <TableRow>
                  <TableCell
                    padding="checkbox"
                    className="app-table-header-cell"
                    style={{ width: TICK_COL_WIDTH, minWidth: TICK_COL_WIDTH }}
                  >
                    <Checkbox
                      size="small"
                      checked={allTicked}
                      indeterminate={someTicked}
                      disabled={displayRows.length === 0}
                      onChange={toggleAll}
                    />
                  </TableCell>
                  {TABLE_COLUMNS.map((col) => {
                    const key = String(col.key)
                    const w = getWidth(key) ?? col.width
                    return (
                      <TableCell
                        key={key}
                        align={col.align}
                        className="app-table-header-cell"
                        onClick={() => toggleSort(col.key)}
                        style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                        sx={{
                          whiteSpace: 'nowrap',
                          cursor: 'pointer',
                          userSelect: 'text',
                          position: 'relative',
                        }}
                      >
                        {col.label}
                        {getTableSortSuffix(sortState, col.key)}
                        <ColumnFilterIcon
                          active={columnFilters.isActive(key)}
                          onClick={(position) => setColPopover({ key: col.key, position })}
                          ariaLabel={`Filter ${col.label}`}
                        />
                        <ResizeHandle onMouseDown={(e) => startResize(key, e)} />
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              itemContent={(_, row) => (
                <>
                  <TableCell
                    padding="checkbox"
                    style={{ width: TICK_COL_WIDTH, minWidth: TICK_COL_WIDTH }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Checkbox
                      size="small"
                      checked={tickedIds.has(row.invoice_id)}
                      onChange={() => toggleRow(row.invoice_id)}
                    />
                  </TableCell>
                  {TABLE_COLUMNS.map((col) => {
                    const key = String(col.key)
                    const w = getWidth(key) ?? col.width
                    const val = cellValue(row, col)
                    const wrap = col.wrap || getWidth(key) != null
                    return (
                      <TableCell
                        key={key}
                        align={col.align}
                        style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                        sx={
                          wrap
                            ? { whiteSpace: 'normal', overflowWrap: 'anywhere', py: 0.5, verticalAlign: 'top' }
                            : { verticalAlign: 'top' }
                        }
                      >
                        <Box
                          component="span"
                          className={wrap ? 'app-table-cell-wrap' : 'app-table-cell-text'}
                          title={wrap ? undefined : val || '-'}
                        >
                          {val || '-'}
                        </Box>
                      </TableCell>
                    )
                  })}
                </>
              )}
            />
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  )
}
